import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Search, Filter, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import logger from '../../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  change_password: 'bg-yellow-100 text-yellow-800',
};

const AdminActivityLogTab = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ module: '', user_email: '', action: '' });
  const limit = 25;

  const fetchLog = useCallback(async (skip = 0) => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (filters.module) params.module = filters.module;
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.action) params.action = filters.action;
      const res = await axios.get(`${BACKEND_URL}/api/admin/activity-log`, { params, withCredentials: true });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchLog(page * limit); }, [fetchLog, page]);

  const handleFilter = () => { setPage(0); fetchLog(0); };

  const totalPages = Math.ceil(total / limit);

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const getActionColor = (action) => {
    for (const [key, cls] of Object.entries(ACTION_COLORS)) {
      if (action.includes(key)) return cls;
    }
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="activity-log-tab">
      <div>
        <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Activity Log</h2>
        <p className="text-xs text-[#4B5563]">{total} total entries</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="activity-log-filters">
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Module</label>
          <select value={filters.module} onChange={(e) => setFilters(f => ({ ...f, module: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none">
            <option value="">All Modules</option>
            <option value="users">Users</option>
            <option value="events">Events</option>
            <option value="testimonials">Testimonials</option>
            <option value="updates">Updates</option>
            <option value="finance">Finance</option>
            <option value="profile">Profile</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">User</label>
          <input type="text" placeholder="Search by email" value={filters.user_email}
            onChange={(e) => setFilters(f => ({ ...f, user_email: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none w-48" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#4B5563] mb-1">Action</label>
          <input type="text" placeholder="e.g. create, delete" value={filters.action}
            onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))}
            className="px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:outline-none w-40" />
        </div>
        <button onClick={handleFilter} className="btn-primary text-xs px-4 py-2 flex items-center gap-1">
          <Filter className="w-3.5 h-3.5" /> Filter
        </button>
        <button onClick={() => { setFilters({ module: '', user_email: '', action: '' }); setPage(0); }}
          className="btn-secondary text-xs px-4 py-2">Clear</button>
      </div>

      {/* Entries */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : entries.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
          <Clock className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
          <p className="text-sm text-[#4B5563]">No activity log entries found</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="divide-y divide-[#F1F5F9]">
            {entries.map((e) => (
              <div key={e.id} className="px-4 py-3 flex items-start gap-3" data-testid={`log-entry-${e.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#F8F9FA] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="w-3.5 h-3.5 text-[#4B5563]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${getActionColor(e.action)}`}>
                      {e.action}
                    </span>
                    <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{e.module}</span>
                    {e.record_id && <span className="text-[10px] text-[#9CA3AF]">ID: {e.record_id}</span>}
                  </div>
                  <p className="text-xs text-[#0A0A0A]">
                    <strong>{e.user_email}</strong>
                    <span className="text-[#9CA3AF]"> ({e.user_role})</span>
                  </p>
                  {e.details && Object.keys(e.details).length > 0 && (
                    <div className="mt-1 text-[10px] text-[#4B5563] bg-[#F8F9FA] rounded px-2 py-1">
                      {Object.entries(e.details).map(([key, val]) => (
                        <span key={key} className="mr-3">
                          <strong>{key}:</strong>{' '}
                          {typeof val === 'object' && val !== null
                            ? `${val.from || '—'} → ${val.to || '—'}`
                            : String(val)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap flex-shrink-0">
                  {formatTime(e.timestamp)}
                </span>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB] bg-[#F8F9FA]">
              <span className="text-xs text-[#4B5563]">Page {page + 1} of {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="p-1.5 border border-[#E5E7EB] rounded disabled:opacity-30">
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  className="p-1.5 border border-[#E5E7EB] rounded disabled:opacity-30">
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminActivityLogTab;
